import React from "react";
import { Button, Form, Modal } from "react-bootstrap";
import "./ExternalApiModal.scss";

const VerificationCodeField = ({
  id, label, value, verified, busy, onChange, onVerify, actionLabel,
  actionDisabled, placeholder, hint, children,
}) => (
  <Form.Group controlId={id} className="external-api-modal__field">
    <Form.Label>{label}</Form.Label>
    <div className="external-api-modal__code-row">
      <Form.Control
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        disabled={busy || verified}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        aria-describedby={hint ? `${id}-hint` : undefined}
      />
      <Button
        variant="light"
        className={`external-api-modal__button external-api-modal__button--${verified ? "verified" : "verify"}`}
        disabled={busy || verified || actionDisabled}
        onClick={onVerify}
        aria-label={`${actionLabel}: ${label}`}
      >
        {verified && <i className="fas fa-check" aria-hidden="true" />}
        {actionLabel}
      </Button>
    </div>
    {children}
    {hint && <small id={`${id}-hint`} className="external-api-modal__hint">{hint}</small>}
  </Form.Group>
);

const ExternalApiVerificationModal = ({
  allowedIp, busy, show, title, onCancel, onChangeAllowedIp, onSendEmailOtp,
  onSubmit, onVerifyEmailOtp, onVerifyGoogleOtp, t, verification,
}) => {
  const googleMinutes = Math.floor(verification.googleSecondsLeft / 60);
  const googleSeconds = verification.googleSecondsLeft % 60;
  const emailActionLabel = verification.verified.email
    ? t("developer-api-verified")
    : t(verification.otpSent ? "developer-api-verify" : "developer-api-send-code");

  return (
    <Modal
      show={show}
      onHide={onCancel}
      backdrop="static"
      keyboard={!busy}
      centered
      dialogClassName="external-api-modal"
      aria-labelledby="external-api-verification-title"
    >
      <Modal.Header closeButton={!busy} closeLabel={t("close")}>
        <Modal.Title id="external-api-verification-title">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body aria-busy={busy}>
        <div className="external-api-modal__notice">
          <i className="fas fa-shield-alt" aria-hidden="true" />
          <p id="external-api-ip-notice">{t("developer-api-ip-notice")}</p>
        </div>
        <Form.Group controlId="external-api-allowed-ip" className="external-api-modal__field">
          <Form.Label>{t("developer-api-allowed-public-ip")}</Form.Label>
          <Form.Control
            value={allowedIp}
            disabled={busy}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => onChangeAllowedIp(event.target.value)}
            placeholder={t("developer-api-ip-placeholder")}
            aria-describedby="external-api-ip-notice"
          />
        </Form.Group>
        <VerificationCodeField
          id="external-api-email-code"
          label={t("developer-api-email-code-label")}
          value={verification.codes.email_otp}
          verified={verification.verified.email}
          busy={busy}
          onChange={(value) => verification.setCode("email_otp", value)}
          onVerify={verification.otpSent ? onVerifyEmailOtp : onSendEmailOtp}
          actionLabel={emailActionLabel}
          placeholder={t("developer-api-code-placeholder")}
          actionDisabled={verification.otpSent && !verification.codes.email_otp}
        >
          {verification.otpSent && !verification.verified.email && (
            <Button className="external-api-modal__resend" variant="link" disabled={busy} onClick={onSendEmailOtp}>
              {t("developer-api-resend-email-code")}
            </Button>
          )}
        </VerificationCodeField>
        <VerificationCodeField
          id="external-api-google-code"
          label={t("developer-api-google-code-label")}
          value={verification.codes.google_otp}
          verified={verification.verified.google}
          busy={busy}
          onChange={(value) => verification.setCode("google_otp", value)}
          onVerify={onVerifyGoogleOtp}
          actionLabel={t(verification.verified.google ? "developer-api-verified" : "developer-api-verify")}
          placeholder={t("developer-api-code-placeholder")}
          actionDisabled={!verification.codes.google_otp}
          hint={verification.verified.google
            ? t("developer-api-google-expiry", { minutes: googleMinutes, seconds: googleSeconds })
            : t("developer-api-google-valid-hint")}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" className="external-api-modal__button external-api-modal__button--cancel" onClick={onCancel} disabled={busy}>
          {t("cancel")}
        </Button>
        <Button
          variant="primary"
          className="external-api-modal__button external-api-modal__button--confirm"
          onClick={onSubmit}
          disabled={busy || !allowedIp.trim() || !verification.verified.email || !verification.verified.google}
        >
          {busy ? t("developer-api-saving") : t("developer-api-confirm")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExternalApiVerificationModal;
